import React, { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { Menu, MenuItem } from '@mui/material'
import { AppServices } from 'middleware'
import { NotificationsServiceContext } from 'settings/context'
import { NotificationItem } from 'settings/notificationsService'
import {
    notificationActionPermission,
    notificationActionUrl,
    notificationTimeLabel,
} from 'settings/notifications/notificationItem'
import { markNotificationsSeen, readNotificationsLastSeen } from 'settings/notifications/notificationsLastSeen'
import { usePermission } from 'admin/hocs'
import { LucideIcon } from 'utils/lucideIcon'
import { SkeletonBar } from 'uiComponents/skeleton/skeleton'
import { useTranslation } from 'translations'
import './notificationsMenu.scss'

const PREVIEW_COUNT = 5

type LoadState = 'loading' | 'ready' | 'error'

interface NotificationsMenuProps {
    accountSlug: string
}

export function NotificationsMenu({ accountSlug }: NotificationsMenuProps) {
    const { t } = useTranslation()
    const notificationsService = useContext(NotificationsServiceContext)
    const { hasPermission } = usePermission()
    const history = useHistory()
    const location = useLocation()
    const [anchor, setAnchor] = useState<HTMLElement | null>(null)
    const [unreadCount, setUnreadCount] = useState(0)
    const [loadState, setLoadState] = useState<LoadState>('loading')
    const [items, setItems] = useState<NotificationItem[]>([])
    const [seenBefore, setSeenBefore] = useState<string | null>(null)
    const feedPath = `/account/${accountSlug}/notifications/feed`

    useEffect(() => {
        notificationsService
            .getNewNotificationsCount(accountSlug)
            .then(setUnreadCount)
            .catch(() => setUnreadCount(0))
    }, [accountSlug])

    useEffect(() => {
        if (/\/feed\b/.test(location.pathname)) {
            setUnreadCount(0)
        }
    }, [location.pathname])

    const load = async () => {
        setLoadState('loading')
        try {
            const data = await notificationsService.getNotificationsForAccount(accountSlug)
            setItems(data.slice(0, PREVIEW_COUNT))
            setLoadState('ready')
        } catch {
            setLoadState('error')
        }
    }

    const open = (event: React.MouseEvent<HTMLElement>) => {
        setAnchor(event.currentTarget)
        setSeenBefore(readNotificationsLastSeen(accountSlug))
        markNotificationsSeen(accountSlug)
        setUnreadCount(0)
        load()
    }

    const goTo = (path: string) => {
        setAnchor(null)
        AppServices.loggingService.logAction('navigation', { click_to: path, source: 'notifications_menu' })
        history.push(path)
    }

    const itemPath = (item: NotificationItem) => {
        const urlParts = item.metadata.urlParts
        return urlParts && hasPermission(notificationActionPermission(urlParts), accountSlug)
            ? notificationActionUrl(urlParts)
            : feedPath
    }

    const isNew = (item: NotificationItem) => String(item.createdAt) > (seenBefore ?? '')

    return (
        <>
            <button
                type="button"
                className={classNames('top-bar-action', { bubble: unreadCount > 0 })}
                aria-haspopup="menu"
                aria-expanded={!!anchor}
                aria-label={t('header.topBar.notifications')}
                title={t('header.topBar.notifications')}
                onClick={open}
            >
                <LucideIcon iconName="Bell" size={18} />
            </button>
            <Menu
                className="notifications-menu"
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <li className="notifications-menu-title">{t('header.topBar.notifications')}</li>
                {loadState === 'loading' &&
                    [0, 1, 2].map((row) => (
                        <li key={row} className="notifications-menu-skeleton" aria-hidden>
                            <SkeletonBar width="30%" height="0.75rem" />
                            <SkeletonBar width="85%" height="0.875rem" />
                            <SkeletonBar width="60%" height="0.875rem" />
                        </li>
                    ))}
                {loadState === 'error' && (
                    <li className="notifications-menu-message">
                        {t('header.topBar.notificationsMenu.error')}
                        <button type="button" className="notifications-menu-retry" onClick={load}>
                            {t('header.topBar.notificationsMenu.retry')}
                        </button>
                    </li>
                )}
                {loadState === 'ready' && items.length === 0 && (
                    <li className="notifications-menu-message">{t('header.topBar.notificationsMenu.empty')}</li>
                )}
                {loadState === 'ready' &&
                    items.map((item, index) => (
                        <MenuItem
                            key={`${item.createdAt}-${index}`}
                            className={classNames('notifications-menu-item', { new: isNew(item) })}
                            onClick={() => goTo(itemPath(item))}
                        >
                            <span className="notifications-menu-item-meta">
                                {item.level === 'Alarm' && (
                                    <span className="notifications-menu-item-urgent">
                                        {t('settings.notifications.feed.urgent')}
                                    </span>
                                )}
                                <span>{notificationTimeLabel(item.createdAt)}</span>
                                {isNew(item) && (
                                    <span className="notifications-menu-item-new">
                                        {t('header.topBar.notificationsMenu.new')}
                                    </span>
                                )}
                            </span>
                            <span className="notifications-menu-item-title">
                                {item.metadata.title} <strong>{item.metadata.subtitle}</strong>
                            </span>
                            {item.metadata.text && (
                                <span className="notifications-menu-item-text">{item.metadata.text}</span>
                            )}
                        </MenuItem>
                    ))}
                <MenuItem className="notifications-menu-all" onClick={() => goTo(feedPath)}>
                    {t('header.topBar.notificationsMenu.seeAll')}
                    <LucideIcon iconName="ArrowRight" size={16} />
                </MenuItem>
            </Menu>
        </>
    )
}
